import { useCallback, useState } from 'react'

import type {
  SetupStatus,
} from '../../types'
import { useToast } from '../useToast'
import { useI18n } from '../../i18n'
import { requestSetupCompletion, requestSetupStatus, requestSetupValidation } from './setupWizardApi'
import { buildSetupWizardPayload, clearSetupWizardQuery } from './useSetupWizard.helpers'
import { clearAuthToken } from '../../lib/api/authToken'
import { getAdminLoginPath } from './adminPath'
import { useSetupWizardForm } from './useSetupWizardForm'

const useSetupWizard = () => {
  const { t } = useI18n()
  const { showToast } = useToast()
  const [setupStatus, setSetupStatus] = useState<SetupStatus | null>(null)
  const [setupLoading, setSetupLoading] = useState(true)
  const [setupSubmitting, setSetupSubmitting] = useState(false)
  const [setupError, setSetupError] = useState<string | null>(null)
  const [setupSuccessVisible, setSetupSuccessVisible] = useState(false)
  const [setupValidating, setSetupValidating] = useState(false)
  const {
    setupForm,
    setupValidationSummary,
    setSetupValidationSummary,
    updateOwnerField,
    updateStoreIdentityField,
    updateOnboardingPreferenceField,
    updatePaymentSetupField,
    updateEmailSetupField,
    updateBotProtectionSetupField,
    resetSetupForm,
  } = useSetupWizardForm()

  const refreshStatus = useCallback(async () => {
    setSetupLoading(true)
    setSetupError(null)
    try {
      const status = await requestSetupStatus({ t })
      setSetupStatus(status)
    } catch (err) {
      const message = err instanceof Error ? err.message : t('setup.toast.statusLoadError')
      setSetupError(message)
      showToast('error', t('setup.validation.title'), message)
    } finally {
      setSetupLoading(false)
    }
  }, [showToast, t])

  const validateConfiguration = useCallback(async () => {
    if (setupValidating) return
    setSetupValidating(true)
    setSetupError(null)
    try {
      const payload = buildSetupWizardPayload(setupForm)
      const validation = await requestSetupValidation({ payload, t })
      setSetupValidationSummary(validation)
      if (validation.status === 'ok') {
        showToast('success', t('setup.validation.title'), t('setup.toast.validationOk'))
      } else if (validation.status === 'warning') {
        showToast('error', t('setup.validation.title'), t('setup.toast.validationWarning'))
      } else {
        showToast('error', t('setup.validation.title'), t('setup.toast.validationError'))
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : t('setup.toast.validationError')
      setSetupError(message)
      showToast('error', t('setup.validation.title'), message)
    } finally {
      setSetupValidating(false)
    }
  }, [setSetupValidationSummary, setupForm, setupValidating, showToast, t])

  const completeSetup = useCallback(async () => {
    if (setupSubmitting) return
    setSetupSubmitting(true)
    setSetupError(null)
    try {
      const payload = buildSetupWizardPayload(setupForm)
      const result = await requestSetupCompletion({
        payload,
        t,
      })
      setSetupStatus(result.status)
      setSetupValidationSummary(result.validation ?? null)
      clearAuthToken()
      clearSetupWizardQuery()
      setSetupSuccessVisible(true)

      showToast('success', t('setup.toast.completed'))
    } catch (err) {
      const message = err instanceof Error ? err.message : t('setup.toast.completeError')
      setSetupError(message)
      showToast('error', t('setup.validation.title'), message)
    } finally {
      setSetupSubmitting(false)
    }
  }, [setSetupValidationSummary, setupForm, setupSubmitting, showToast, t])

  const goToLogin = useCallback(() => {
    clearAuthToken()
    clearSetupWizardQuery()
    if (typeof window !== 'undefined') {
      window.location.assign(getAdminLoginPath())
    }
  }, [])

  return {
    setupStatus,
    setupLoading,
    setupSubmitting,
    setupError,
    setupValidationSummary,
    setupValidating,
    setupSuccessVisible,
    setupForm,
    updateOwnerField,
    updateStoreIdentityField,
    updateOnboardingPreferenceField,
    updatePaymentSetupField,
    updateEmailSetupField,
    updateBotProtectionSetupField,
    resetSetupForm,
    refreshStatus,
    validateConfiguration,
    completeSetup,
    goToLogin,
  }
}

export default useSetupWizard
